'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { onBackButton } from '@/lib/utils/deep-linking'

const OPEN_OVERLAY = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  // Radix DropdownMenu / Select content, and the classic image lightbox.
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[data-overlay="lightbox"]',
].join(', ')

/**
 * Dialogs and sheets push no history entry, so Back used to leave the page behind an
 * open one. Ask the topmost layer to close the way a keyboard user would (Escape,
 * which Radix handles and which a modal can still veto). Returns true when an
 * overlay was open, so the caller must not navigate or minimize.
 */
export function closeTopOverlay(doc: Document = document): boolean {
  if (!doc.querySelector(OPEN_OVERLAY)) return false
  const target = doc.activeElement ?? doc.body
  target.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true })
  )
  return true
}

/**
 * Wire Android hardware back button to Next.js router.
 *
 * - If a dialog or sheet is open: close it first
 * - If there's history: go back
 * - If at root: minimize the app. Registering any `backButton` listener
 *   disables Capacitor's default behavior, so the root case must be explicit
 *   or Back does nothing.
 *
 * Drop this into the dashboard shell or root layout.
 */
export function useNativeBackButton() {
  const router = useRouter()

  useEffect(() => {
    let cancelled = false
    let cleanup: (() => void) | undefined

    onBackButton(async (canGoBack) => {
      if (closeTopOverlay()) return
      if (canGoBack) {
        router.back()
        return
      }
      try {
        const { App } = await import('@capacitor/app')
        await App.minimizeApp()
      } catch {
        // Not on a native shell: nothing to minimize.
      }
    }).then((fn) => {
      // Registration resolves asynchronously; if the component already
      // unmounted, remove the listener now instead of leaking it.
      if (cancelled) fn()
      else cleanup = fn
    })

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [router])
}
